import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Download, FileText, AlertTriangle, CheckCircle } from "lucide-react";

const API = process.env.REACT_APP_API || "";

export function ImportExportView() {
  const [mode, setMode] = useState("import");
  const [importTypes, setImportTypes] = useState([]);
  const [exportTypes, setExportTypes] = useState([]);
  const [exportSummary, setExportSummary] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [csvData, setCsvData] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const headers = useCallback(() => { const t = localStorage.getItem("token") || sessionStorage.getItem("token"); return { Authorization: `Bearer ${t}` }; }, []);

  useEffect(() => {
    (async () => {
      try {
        const [it, et, es] = await Promise.all([
          axios.get(`${API}/api/import-export/import/types`, { headers: headers() }),
          axios.get(`${API}/api/import-export/export/types`, { headers: headers() }),
          axios.get(`${API}/api/import-export/export/summary`, { headers: headers() }),
        ]);
        setImportTypes(it.data || []);
        setExportTypes(et.data || []);
        setExportSummary(es.data || []);
      } catch (e) { console.error(e); }
    })();
  }, [headers]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvData(ev.target.result);
    reader.readAsText(file);
  };

  const analyzeData = async () => {
    if (!csvData || !selectedType) { toast.error("Bitte Datei und Typ wählen"); return; }
    try {
      const r = await axios.post(`${API}/api/import-export/import/analyze`, { csv_data: csvData, target_type: selectedType }, { headers: headers() });
      setAnalysis(r.data);
      setMapping(r.data.mapping_suggestions || {});
    } catch (e) { toast.error("Analyse fehlgeschlagen"); }
  };

  const executeImport = async () => {
    if (!analysis) return;
    setImporting(true);
    try {
      // Parse CSV to rows with mapping
      const lines = csvData.split("\n");
      const headerLine = lines[0].split(",").map(h => h.trim().replace(/"/g, ""));
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        const vals = lines[i].split(",").map(v => v.trim().replace(/"/g, ""));
        const row = {};
        headerLine.forEach((h, idx) => { row[h] = vals[idx] || ""; });
        rows.push(row);
      }
      const r = await axios.post(`${API}/api/import-export/import/execute`, { target_type: selectedType, data: rows, mapping, skip_duplicates: true }, { headers: headers() });
      setResult(r.data);
      toast.success(`${r.data.imported} Einträge importiert`);
    } catch (e) { toast.error("Import fehlgeschlagen"); }
    setImporting(false);
  };

  const exportData = async (type) => {
    try {
      const r = await axios.post(`${API}/api/import-export/export`, { type, format: "csv" }, { headers: headers() });
      if (r.data.csv) {
        const blob = new Blob([r.data.csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `${type}_export.csv`; a.click();
        toast.success(`${r.data.count} Einträge exportiert`);
      } else { toast.info("Keine Daten vorhanden"); }
    } catch (e) { toast.error("Export fehlgeschlagen"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button onClick={() => setMode("import")} className={`px-4 py-2 rounded-md text-sm font-medium ${mode === "import" ? "bg-[var(--brand-primary)] text-white" : "bg-gray-100"}`}><Upload className="w-4 h-4 inline mr-1" />Import</button>
        <button onClick={() => setMode("export")} className={`px-4 py-2 rounded-md text-sm font-medium ${mode === "export" ? "bg-[var(--brand-primary)] text-white" : "bg-gray-100"}`}><Download className="w-4 h-4 inline mr-1" />Export & Datensicherung</button>
      </div>

      {mode === "import" && (
        <div className="space-y-4">
          <Card className="p-4 space-y-3">
            <h3 className="font-semibold">Daten importieren</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-500">Zieltyp</label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger><SelectValue placeholder="Was importieren?" /></SelectTrigger>
                  <SelectContent>{importTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-gray-500">CSV-Datei</label>
                <Input type="file" accept=".csv,.txt" onChange={handleFileUpload} />
              </div>
            </div>
            {csvData && <Button onClick={analyzeData}>Datei analysieren</Button>}
          </Card>

          {analysis && (
            <Card className="p-4 space-y-3">
              <h3 className="font-semibold">Analyse: {analysis.total_rows} Zeilen erkannt</h3>
              <div className="space-y-2">
                <p className="text-sm text-gray-500">Spalten-Zuordnung:</p>
                {analysis.detected_columns.map(col => (
                  <div key={col} className="flex items-center gap-2">
                    <span className="text-sm w-32 truncate">{col}</span>
                    <span className="text-gray-400">→</span>
                    <Select value={mapping[col] || ""} onValueChange={v => setMapping({...mapping, [col]: v})}>
                      <SelectTrigger className="w-48"><SelectValue placeholder="Zuordnen..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">– nicht importieren –</SelectItem>
                        {analysis.target_fields.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <Button onClick={executeImport} disabled={importing}>{importing ? "Importiere..." : "Import starten"}</Button>
            </Card>
          )}

          {result && (
            <Card className="p-4">
              <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500" /><span className="font-semibold">Import abgeschlossen</span></div>
              <p className="text-sm mt-1">Importiert: {result.imported} | Übersprungen: {result.skipped} | Fehler: {result.errors?.length || 0}</p>
            </Card>
          )}
        </div>
      )}

      {mode === "export" && (
        <div className="space-y-3">
          <h3 className="font-semibold">Daten exportieren</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {exportSummary.map(t => (
              <Card key={t.id} className="p-3 flex items-center justify-between">
                <div><div className="font-medium text-sm">{t.label}</div><div className="text-xs text-gray-400">{t.count} Einträge</div></div>
                <Button size="sm" variant="outline" onClick={() => exportData(t.id)} disabled={t.count === 0}><Download className="w-3 h-3 mr-1" />CSV</Button>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
